import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  OnInit,
  inject,
  isDevMode,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';

import {
  IBatchShootArchiveTarget,
  IShootAssetArchiveInfo,
  IShootAssetArchiveResponse,
} from 'src/app/models/batches/batchArchive';
import {
  PreviewShootAsset,
  PreviewShootAssetArchiveResponse,
  ShootAssetArchiveProductData,
} from 'src/app/models/batches/shoot-asset-archive-preview';
import { IProduct } from 'src/app/models/products/product';
import { MediaType } from 'src/app/models/products/asset';
import { ExtraRow } from 'src/app/components/shared/product-identifiers-popover/product-identifiers-popover.component';
import { AlertService } from 'src/app/services/alert.service';
import { AssetsService } from 'src/app/services/assets.service';

export interface ShootAssetArchiveDialogData {
  readonly productIds: string[];
  readonly organizationId: string;
  readonly batchId: string;
}

export interface ProductRowView {
  readonly data: ShootAssetArchiveProductData;
  readonly isBlocked: boolean;
  readonly filteredStills: readonly PreviewShootAsset[];
  readonly filteredVideos: readonly PreviewShootAsset[];
  readonly filteredTiles: readonly TileView[];
  readonly stillsCount: number;
  readonly videosCount: number;
  readonly popoverProduct: IProduct;
  readonly extraRows: readonly ExtraRow[];
}

export interface TileView {
  readonly id: string;
  readonly mediaType: MediaType;
  readonly thumbnailUri: string;
  readonly mstaCode: string;
}

// Generic surface-message for unexpected transport failures. Domain errors flow through
// the discriminated response and reuse `error.message` verbatim; this constant is only
// reached when the observable itself errors (network / 5xx / etc). ErrorInterceptor
// already toasts the HTTP root-cause; this string powers the dialog's inline banner.
const HTTP_ERROR_MESSAGE = 'Archive job failed.';
const PREVIEW_ERROR_MESSAGE = 'Failed to load archive preview.';
const CREATE_SUCCESS_MESSAGE = "Archive job started — you'll be notified when it's ready.";

@Component({
  selector: 'app-shoot-asset-archive-dialog',
  templateUrl: './shoot-asset-archive-dialog.component.html',
  styleUrl: './shoot-asset-archive-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class ShootAssetArchiveDialogComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  preview: PreviewShootAssetArchiveResponse | null = null;
  isLoading = false;
  isSubmitting = false;
  errorMessage: string | null = null;

  readonly expandedProducts: Set<string> = new Set<string>();
  readonly selectedMediaTypes: Set<MediaType> = new Set<MediaType>(['Still', 'Video']);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ShootAssetArchiveDialogData,
    public dialogRef: MatDialogRef<ShootAssetArchiveDialogComponent, IShootAssetArchiveInfo | null>,
    private assetsService: AssetsService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.loadPreview();
  }

  loadPreview(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.assetsService
      .previewShootAssetArchive({
        productIds: this.data.productIds,
        organizationId: this.data.organizationId,
        batchId: this.data.batchId,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (response: PreviewShootAssetArchiveResponse) => {
          this.preview = response;
        },
        error: (err: unknown) => {
          if (isDevMode()) {
            console.error('[DEV] [ShootAssetArchiveDialogComponent] preview failed', err);
          }
          this.preview = null;
          this.errorMessage = PREVIEW_ERROR_MESSAGE;
        },
      });
  }

  // --- Derived view-model ---

  get rows(): readonly ProductRowView[] {
    return (this.preview?.products ?? []).map((data) => this.buildRowView(data));
  }

  get productsWithAssetsCount(): number {
    return (this.preview?.products ?? []).filter((data) => !this.isBlocked(data)).length;
  }

  get totalProductsCount(): number {
    return this.preview?.products.length ?? 0;
  }

  get allHaveAssets(): boolean {
    return this.totalProductsCount > 0 && this.productsWithAssetsCount === this.totalProductsCount;
  }

  get totalStillsCount(): number {
    return (this.preview?.products ?? []).reduce(
      (sum, data) => sum + (data.assets?.stills?.length ?? 0),
      0,
    );
  }

  get totalVideosCount(): number {
    return (this.preview?.products ?? []).reduce(
      (sum, data) => sum + (data.assets?.videos?.length ?? 0),
      0,
    );
  }

  get anyExpanded(): boolean {
    return this.expandedProducts.size > 0;
  }

  get isCreateDisabled(): boolean {
    if (this.isSubmitting) {
      return true;
    }
    if (this.selectedMediaTypes.size === 0) {
      return true;
    }
    // At least one non-blocked product must contribute assets under the current filter.
    return this.rows.every((row) => row.isBlocked || row.filteredTiles.length === 0);
  }

  isProductExpanded(productId: string): boolean {
    return this.expandedProducts.has(productId);
  }

  // --- User actions ---

  toggleRow(row: ProductRowView): void {
    if (row.isBlocked) {
      return;
    }
    const productId: string = row.data.product.id;
    if (this.expandedProducts.has(productId)) {
      this.expandedProducts.delete(productId);
      return;
    }
    this.expandedProducts.add(productId);
  }

  toggleAll(): void {
    if (this.anyExpanded) {
      this.expandedProducts.clear();
      return;
    }
    (this.preview?.products ?? [])
      .filter((data) => !this.isBlocked(data))
      .forEach((data) => this.expandedProducts.add(data.product.id));
  }

  toggleMediaType(type: MediaType): void {
    if (this.selectedMediaTypes.has(type)) {
      this.selectedMediaTypes.delete(type);
      return;
    }
    this.selectedMediaTypes.add(type);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onCreate(): void {
    if (this.isSubmitting) {
      return;
    }
    // Canonical order: 'Still' before 'Video'. Predictable emit shape.
    const mediaTypes: MediaType[] = (['Still', 'Video'] as const).filter((t) =>
      this.selectedMediaTypes.has(t),
    );
    const target: IBatchShootArchiveTarget = { mediaTypes };

    this.isSubmitting = true;
    this.errorMessage = null;

    this.assetsService
      .createShootAssetArchive({
        productIds: this.data.productIds,
        organizationId: this.data.organizationId,
        batchId: this.data.batchId,
        target,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: IShootAssetArchiveResponse) => this.handleCreateResponse(response),
        error: (err: unknown) => this.handleCreateError(err),
      });
  }

  trackByProductId(_i: number, row: ProductRowView): string {
    return row.data.product.id;
  }

  trackByTileId(_i: number, tile: TileView): string {
    return tile.id;
  }

  // --- Private helpers ---

  private isBlocked(data: ShootAssetArchiveProductData): boolean {
    return data.enhancementVariant === null;
  }

  private buildRowView(data: ShootAssetArchiveProductData): ProductRowView {
    const blocked: boolean = this.isBlocked(data);
    const stillsSelected: boolean = this.selectedMediaTypes.has('Still');
    const videosSelected: boolean = this.selectedMediaTypes.has('Video');
    const filteredStills: readonly PreviewShootAsset[] =
      stillsSelected ? (data.assets?.stills ?? []) : [];
    const filteredVideos: readonly PreviewShootAsset[] =
      videosSelected ? (data.assets?.videos ?? []) : [];
    const filteredTiles: readonly TileView[] = [
      ...filteredStills.map((asset) => this.buildTile(asset, 'Still')),
      ...filteredVideos.map((asset) => this.buildTile(asset, 'Video')),
    ];
    return {
      data,
      isBlocked: blocked,
      filteredStills,
      filteredVideos,
      filteredTiles,
      stillsCount: filteredStills.length,
      videosCount: filteredVideos.length,
      popoverProduct: this.toIProduct(data),
      extraRows: this.buildExtraRows(data),
    };
  }

  private buildTile(asset: PreviewShootAsset, mediaType: MediaType): TileView {
    return {
      id: asset.shootAsset.id,
      mediaType,
      thumbnailUri: asset.thumbnail.uri,
      mstaCode: asset.masterSuiteTouchAsset.code,
    };
  }

  private buildExtraRows(data: ShootAssetArchiveProductData): readonly ExtraRow[] {
    return [
      { label: 'Master Suite', value: data.enhancementVariant?.masterSuite.name ?? null },
      { label: 'Enh. Variant', value: data.enhancementVariant?.name ?? null },
    ];
  }

  private toIProduct(data: ShootAssetArchiveProductData): IProduct {
    // Popover only reads .barcode / .reference / .description / .id. NO_ERRORS_SCHEMA in
    // tests and the popover's own body access these four alone — the rest of IProduct
    // is inert here. Cast keeps the boundary explicit without inventing fake values.
    return {
      id: data.product.id,
      barcode: data.product.barcode,
      reference: data.product.reference,
      description: data.product.description,
    } as unknown as IProduct;
  }

  private handleCreateResponse(response: IShootAssetArchiveResponse): void {
    this.isSubmitting = false;
    if (response.type === 'error') {
      this.errorMessage = response.error.message;
      this.alertService.errorToast(response.error.message);
      return;
    }
    this.alertService.successToast(CREATE_SUCCESS_MESSAGE);
    this.dialogRef.close(response.archive);
  }

  private handleCreateError(err: unknown): void {
    if (isDevMode()) {
      console.error('[DEV] [ShootAssetArchiveDialogComponent] archive create failed', err);
    }
    // HTTP errors are handled globally by ErrorInterceptor (see style-typescript.md).
    // Component only surfaces the inline banner and clears in-flight state.
    this.isSubmitting = false;
    this.errorMessage = HTTP_ERROR_MESSAGE;
  }
}
